import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { IColumnHeader } from "@app/main/components/reports/models/IColumnHeader";
import { TranslationService } from "@app/services/translation.service";
import { firstValueFrom } from "rxjs";
import { IDashboard } from "./_models/IDashboard";
import { IDashboardWidget } from "./_models/IDashboardWidget";
import { IStandardDashboardsDto } from "./_models/IStandardDashboardsDto";

export type DashboardNavItem = {
    dashboard: IDashboard;
    id: string;
}

@Injectable({
    providedIn: 'root'
})
export class DashboardNavigationService {
    private items: DashboardNavItem[];

    constructor(
        private readonly http: HttpClient,
        private readonly translationService: TranslationService
    ) { }

    async getDefaultId(): Promise<string> {
        await this.loadItems();
        return this.items.find(x => x.dashboard.isDefault)!.id;
    }

    async isIdValid(id?: string): Promise<boolean> {
        if (!id) {
            return false;
        }

        await this.loadItems();
        return this.items.some(x => x.id === id);
    }

    async getNavItems(forceRefresh: boolean = false): Promise<DashboardNavItem[]> {
        await this.loadItems(forceRefresh);
        return this.items;
    }

    private async loadItems(forceRefresh: boolean = false): Promise<void> {
        if (!this.items || forceRefresh) {
            this.items = await this.fetchNavItems();
        }
    }

    private async fetchNavItems(): Promise<DashboardNavItem[]> {
        const dashboards = await this.getStandardDashboards();

        return dashboards.map(dashboard => {
            return {
                dashboard,
                id: dashboard.Title.toLowerCase().replace(" ", "")
            }
        });
    }

    private async getStandardDashboards(): Promise<IDashboard[]> {
        const dashboardsDto = await firstValueFrom(this.http.get<IStandardDashboardsDto>('/api/dashboard/standard'));

        return dashboardsDto.Dashboards.map(dashboard => {
            dashboard.url = dashboard.Title.replace(" ", "-");

            dashboard.Widgets = JSON.parse(dashboard.WidgetsJson) as IDashboardWidget[];

            dashboard.Widgets.forEach(widget => {
                if (widget.DataWidgetParameters) {
                    widget.icon = this.getAppropriateColumnIcon(widget.DataWidgetParameters.DataSource[0]);
                }
            });

            dashboard.isDefault = dashboard.SystemDashboard === dashboardsDto.DefaultDashboard;

            this.translateDashboard(dashboard);
            return dashboard;
        });
    }

    private translateDashboard(dashboard: IDashboard): void {
        dashboard.Title = this.translateConditionally(dashboard.Title);

        dashboard.Widgets.forEach(widget => {
            widget.Title = this.translateConditionally(widget.Title);

            widget.DataWidgetParameters?.DataSource?.forEach(dataSource => {
                dataSource.Title = this.translateConditionally(dataSource.Title);
            })
        })
    }

    private translateConditionally(key: string): string {
        // A lot of data is being translated on backend,
        // causing errors in console. We can check if a given key
        // is actual translation key by checking if it contains a dot.
        if (!key.includes(".")) {
            return key;
        }

        return this.translationService.t(key);
    }

    private getAppropriateColumnIcon(columnHeader: IColumnHeader): string {
        if (!columnHeader) {
            return "fa-chart-simple-horizontal"; // Default fallback
        }

        // Currently icons are not set for the below as they are not passed as column headers.
        //      Camera Events
        //      Live Tacho Warnings
        //      Vehicle Utilisation

        const titleToIconMap: { [key: string]: string } = {
            "Can Distance": "fa-route",
            "Fuel": "fa-gas-pump",
            "Planned Vs Actual": "fa-chart-line",
            "Cost": "fa-dollar-sign",
            "Observations": "fa-eye",
            "Fuel Used": "fa-tachometer-alt",
            "Fuel Efficiency": "fa-gas-pump",
            "Driving Fuel": "fa-car",
            "Contextual Speeding Events": "fa-tachometer-alt",
            "Total Score 2": "fa-star",
            "Major Events Needing Review": "fa-exclamation-triangle",
            "VideosReviewed": "fa-video",
            "Videos Coached": "fa-chalkboard-teacher",
            "Videos Driver Trained": "fa-user-graduate",
            "Videos Praised": "fa-thumbs-up",
            "Videos Unseen": "fa-eye-slash",
            "Videos Currently Requested": "fa-download",
            "Videos Downloaded": "fa-download",
            "Unknown Events 2": "fa-question-circle",
            "Speed Events": "fa-tachometer-alt",
            "Total Score": "fa-star",
            "Idling Score": "fa-stop-circle",
            "Over Rev Score": "fa-exclamation-triangle",
            "Coasting Inclusive Score": "fa-road",
            "Harsh Braking Score": "fa-brake-warning",
            "Visits Count": "fa-users",
            "Schedule Adherence": "fa-calendar-check",
            "Planned Vs Actual Distance": "fa-route",
            "Planned Vs Actual Travel Time Difference": "fa-clock",
            "Planned Vs Actual On Site Time Difference": "fa-clock",
            "Total Planned Distance": "fa-chart-simple",
            "Early Arrival Visits Count": "fa-clock",
            "Completed Count": "fa-check-circle",
        };
        

        for (let title in titleToIconMap) {
            if (columnHeader.DefaultTitle.indexOf(title) >= 0) {
                return titleToIconMap[title];
            }
        }

        return columnHeader.dataSourceDefinition?.icon || "fa-chart-simple-horizontal";
    }
}